import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Subscription } from 'rxjs';
import { UIStore } from '../../services/ui.store';
import { SettingsService } from '../../../features/Settings/services/settings.service';
import { AiTestCommandsService } from '../../../core/services/ai-test-commands/ai-test-commands.service';
import { AssistantActionService } from '../../services/voice-actions/assistant-action.service';
import { VoiceActionRunnerService } from '../../services/voice-actions/voice-action-runner.service';
import { VoiceAssistantRequestContextService } from '../../../features/VoiceAssistant/context/voice-assistant-request-context.service';
import { AuthStore } from '../../auth/state/auth.store';
import { Rights } from '../../constants/rights.constants';
import { JivuNativeSpeechService, NativeSpeechPayload } from '../../../features/JivuShell/services/jivu-native-speech.service';
import { JivuOverlayBusService } from '../../../features/JivuShell/services/jivu-overlay-bus.service';
import { JivuShellService, JivuShellTalkResponse } from '../../../features/JivuShell/services/jivu-shell.service';
import { JivuTalkMemoryService } from '../../../features/JivuShell/services/jivu-talk-memory.service';
import {
  AssistantAction,
  AssistantPendingAction,
  VoiceAssistantLanguage,
  VoiceAssistantMode,
  VoiceActionRunResult
} from '../../services/voice-actions/models';

export type VoiceState = 'idle' | 'listening' | 'processing' | 'speaking' | 'error';

interface PendingVoiceActionConfirmation {
  actions: AssistantAction[];
  replyText?: string;
  promptText: string;
}

interface VoiceAssistantStartWorkDetail {
  promptText?: string;
  docked?: boolean;
}

@Component({
  selector: 'app-voice-assistant',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './voice-assistant.component.html',
  styleUrl: './voice-assistant.component.scss'
})
export class VoiceAssistantComponent implements OnInit, OnDestroy {
  readonly uiStore = inject(UIStore);
  private readonly settingsService = inject(SettingsService);
  private readonly aiTestCommands = inject(AiTestCommandsService);
  private readonly assistantActionService = inject(AssistantActionService);
  private readonly actionRunner = inject(VoiceActionRunnerService);
  private readonly requestContext = inject(VoiceAssistantRequestContextService);
  private readonly authStore = inject(AuthStore);
  private readonly nativeSpeech = inject(JivuNativeSpeechService);
  private readonly overlayBus = inject(JivuOverlayBusService);
  private readonly jivuShell = inject(JivuShellService);
  private readonly talkMemory = inject(JivuTalkMemoryService);

  status = signal<VoiceState>('idle');
  transcript = signal('');
  aiResponse = signal('');
  errorMessage = signal('');
  isDocked = signal(false);
  assistantMode = signal<VoiceAssistantMode | null>(null);
  brandLabel = computed(() => {
    const mode = this.assistantMode();
    if (mode === 'work') return 'Jivu Tools';
    if (mode === 'talk') return 'Jivu Talk';
    return 'hey jivu';
  });
  canUseTalkMode = computed(() => {
    const user = this.authStore.user();
    if (!user) return false;
    if (user.isSuperAdmin) return true;

    return user.rights?.includes(Rights.JivuTalkShell_Access) || false;
  });
  canOpenJivuBar = computed(() => this.assistantMode() === 'talk' && this.isNativeJivuBarAvailable());
  modeChoiceStatus = computed(() => this.canUseTalkMode() ? 'Choose Talk or Tools' : 'Choose Tools');

  private recognition: any;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private pendingAction: AssistantPendingAction | null = null;
  pendingConfirmation: PendingVoiceActionConfirmation | null = null;
  private readonly expandHandler = () => this.expandPopup();
  private readonly startWorkHandler = (event: Event) => {
    void this.startWorkFromExternal(event as CustomEvent<VoiceAssistantStartWorkDetail>);
  };
  private finalTranscriptBuffer = '';
  private silenceTimer: number | undefined;
  private recognitionRestartTimer: number | undefined;
  private nativeTtsWatchdog: ReturnType<typeof globalThis.setTimeout> | undefined;
  private nativeSpeechSubscription: Subscription | null = null;
  private isSending = false;
  private autoRestart = true;
  private noSpeechCount = 0;
  private isPromptingForMode = false;
  private nativeListening = false;
  private nativeSpeaking = false;
  private readonly maxNoSpeechRestarts = 5;

  ngOnInit() {
    window.addEventListener('voice-assistant:expand', this.expandHandler);
    window.addEventListener('voice-assistant:start-work', this.startWorkHandler as EventListener);
    this.nativeSpeechSubscription = this.nativeSpeech.events$.subscribe(payload => this.handleNativeSpeechEvent(payload));
    const requestedMode = this.uiStore.globalMicMode();
    if (requestedMode) {
      this.prepareSession();
      void this.activateMode(requestedMode);
    } else {
      this.startSession();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('voice-assistant:expand', this.expandHandler);
    window.removeEventListener('voice-assistant:start-work', this.startWorkHandler as EventListener);
    this.autoRestart = false;
    this.isPromptingForMode = false;
    this.clearSilenceTimer();
    this.clearRecognitionRestart();
    this.clearNativeTtsWatchdog();
    this.nativeSpeechSubscription?.unsubscribe();
    this.stopListening();
    this.stopSpeaking();
  }

  startSession() {
    this.prepareSession();
    this.assistantMode.set(null);
    this.aiResponse.set(this.modeChoicePrompt());
    void this.speakModeChoicePrompt();
  }

  private prepareSession(): void {
    this.autoRestart = true;
    this.pendingAction = null;
    this.pendingConfirmation = null;
    this.isDocked.set(false);
    this.finalTranscriptBuffer = '';
    this.clearSilenceTimer();
    this.transcript.set('');
    this.errorMessage.set('');
  }

  private initSpeechRecognition() {
    this.clearRecognitionRestart();
    const lang = this.uiStore.brainLanguage();
    const recognitionLanguage = this.resolveRecognitionLanguage(lang);
    if (this.nativeSpeech.start(recognitionLanguage, true, 'passive')) {
      this.nativeListening = true;
      this.status.set('listening');
      this.errorMessage.set('');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      this.status.set('error');
      this.errorMessage.set('Speech recognition is not supported in this browser.');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;

    // Urdu mode uses the Hindi speech engine because browser Urdu support is inconsistent.
    this.recognition.lang = recognitionLanguage;

    this.recognition.onstart = () => {
      this.status.set('listening');
      this.errorMessage.set('');
    };

    this.recognition.onresult = (event: any) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          this.finalTranscriptBuffer = `${this.finalTranscriptBuffer} ${event.results[i][0].transcript}`.trim();
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      const activeText = `${this.finalTranscriptBuffer} ${interimTranscript}`.trim();
      if (activeText) {
        this.transcript.set(activeText);
        this.noSpeechCount = 0;
        this.scheduleAutoSend();
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('STT Error', event);
      if (event.error === 'no-speech') {
        this.handleNoSpeech();
      } else {
        this.status.set('error');
        this.errorMessage.set(`Speech recognition failed: ${event.error}`);
      }
    };

    this.recognition.onend = () => {
      if (this.status() === 'listening' && this.autoRestart) {
        this.scheduleRecognitionRestart(this.noSpeechCount > 0 ? 500 : 0);
      }
    };

    try {
      this.recognition.start();
    } catch (e) {
      console.error('Failed to start recognition', e);
    }
  }

  stopListening() {
    this.clearSilenceTimer();
    this.clearRecognitionRestart();
    if (this.nativeListening) {
      this.nativeListening = false;
      this.nativeSpeech.stop();
    }
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (e) {}
    }
  }

  async sendToBrain() {
    if (this.isSending) return;
    const textToSend = this.transcript().trim();
    if (!textToSend) return;

    this.isSending = true;
    this.autoRestart = false;
    this.clearSilenceTimer();
    this.stopListening();
    this.status.set('processing');
    this.errorMessage.set('');

    try {
      const activeMode = this.assistantMode();
      if (!activeMode) {
        const chosenMode = this.detectModeChoice(textToSend);
        if (chosenMode) {
          await this.activateMode(chosenMode);
          return;
        }

        const prompt = this.modeChoicePrompt();
        this.aiResponse.set(prompt);
        this.autoRestart = true;
        await this.speakResponse(prompt);
        return;
      }

      if (activeMode === 'talk') {
        await this.sendToShellTalk(textToSend);
        return;
      }

      if (this.pendingConfirmation) {
        await this.handlePendingConfirmation(textToSend);
        return;
      }

      const resolveRequest = this.requestContext.buildRequest(textToSend, this.pendingAction, {
        mode: activeMode,
        includeNavigation: true,
        languageOverride: this.resolveRequestLanguage()
      });
      const resolveResponse = await firstValueFrom(this.assistantActionService.resolveIntent(resolveRequest));
      this.pendingAction = resolveResponse?.pendingAction || (resolveResponse?.status === 'partial' ? this.pendingAction : null);

      if (resolveResponse && (resolveResponse.status === 'partial' || resolveResponse.status === 'unsupported')) {
        const response = resolveResponse.replyText
          || resolveResponse.pendingAction?.promptText
          || this.defaultClarificationReply();
        this.aiResponse.set(response);
        this.autoRestart = true;
        await this.speakResponse(response);
        return;
      }

      if (resolveResponse?.status === 'matched' && resolveResponse.replyText && (!resolveResponse.actions || resolveResponse.actions.length === 0)) {
        this.aiResponse.set(resolveResponse.replyText);
        await this.speakResponse(resolveResponse.replyText);
        return;
      }

      const actions = resolveResponse?.actions || [];
      const executableActions = actions.filter(action => !this.requiresConfirmationForAction(action));
      const confirmOnlyActions = actions.filter(action => this.requiresConfirmationForAction(action));
      let actionHandled = false;

      if (executableActions.length > 0) {
        const actionResult = await this.actionRunner.runActions(executableActions);
        actionHandled = await this.handleActionResult(actionResult, resolveResponse?.replyText);
      }

      if (confirmOnlyActions.length > 0) {
        const prompt = this.buildConfirmationPrompt(confirmOnlyActions, resolveResponse?.replyText);
        this.pendingConfirmation = {
          actions: confirmOnlyActions,
          replyText: resolveResponse?.replyText,
          promptText: prompt
        };
        this.aiResponse.set(prompt);
        this.autoRestart = true;
        await this.speakResponse(prompt);
        return;
      }

      if (actionHandled) {
        if (!resolveResponse?.pendingAction) {
          this.pendingAction = null;
        }
        return;
      }

      if (resolveResponse?.status === 'error') {
        const response = resolveResponse.replyText || this.voiceActionUnavailableReply();
        this.aiResponse.set(response);
        await this.speakResponse(response);
        return;
      }

      await this.fallbackToMainBrain(textToSend);
    } catch (error) {
      console.error('Failed to resolve assistant voice action', error);
      const activeMode = this.assistantMode();
      if (activeMode === 'talk') {
        const reply = this.talkConnectionErrorReply();
        this.aiResponse.set(reply);
        await this.speakResponse(reply);
        return;
      }
      if (activeMode === 'work') {
        const reply = this.voiceActionUnavailableReply();
        this.aiResponse.set(reply);
        await this.speakResponse(reply);
        return;
      }
      await this.fallbackToMainBrain(textToSend);
    } finally {
      this.autoRestart = true;
      this.isSending = false;
    }
  }

  openJivuBar(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.assistantMode() !== 'talk' || !this.isNativeJivuBarAvailable()) return;

    const busNonce = this.createOverlayBusNonce();
    this.overlayBus.setTrustedNonce(busNonce);
    const session = { token: this.authStore.token(), busNonce };
    if (window.heyjivuDesktop?.overlay?.show) {
      void window.heyjivuDesktop.overlay.show(session);
      return;
    }

    void window.Capacitor?.Plugins?.JivuOverlay?.show?.(session);
  }

  async selectMode(mode: VoiceAssistantMode): Promise<void> {
    await this.activateMode(mode);
  }

  private async activateMode(mode: VoiceAssistantMode): Promise<void> {
    if (mode === 'talk' && !this.canUseTalkMode()) {
      const reply = this.talkUpgradePrompt();
      this.aiResponse.set(reply);
      this.autoRestart = true;
      await this.speakResponse(reply);
      return;
    }

    this.isPromptingForMode = false;
    this.assistantMode.set(mode);
    this.pendingAction = null;
    this.finalTranscriptBuffer = '';
    this.transcript.set('');
    this.clearSilenceTimer();
    this.stopListening();

    const response = this.modeReadyReply(mode);
    this.aiResponse.set(response);
    this.autoRestart = true;
    await this.speakResponse(response);
  }

  private async startWorkFromExternal(event: CustomEvent<VoiceAssistantStartWorkDetail>): Promise<void> {
    const prompt = event.detail?.promptText?.trim() || this.modeReadyReply('work');

    this.autoRestart = true;
    this.isPromptingForMode = false;
    this.assistantMode.set('work');
    this.pendingAction = null;
    this.pendingConfirmation = null;
    this.finalTranscriptBuffer = '';
    this.transcript.set('');
    this.errorMessage.set('');
    this.aiResponse.set(prompt);
    this.isDocked.set(event.detail?.docked === true);
    this.clearSilenceTimer();
    this.clearRecognitionRestart();
    this.stopListening();
    await this.speakResponse(prompt);
  }

  private detectModeChoice(text: string): VoiceAssistantMode | null {
    const normalized = text
      .toLowerCase()
      .replace(/[^a-z0-9\u0600-\u06ff\u0900-\u097f]+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalized) {
      return null;
    }

    const talkPhrases = [
      'jivu talk',
      'talk mode',
      'just talk',
      'only talk',
      'chat mode',
      'just chat',
      'only chat',
      'sirf baat',
      'baat kar',
      'baatcheet',
      'बात',
      'बातचीत',
      'सिर्फ बात'
    ];
    if (talkPhrases.some(phrase => normalized.includes(phrase))) {
      return 'talk';
    }

    const workPhrases = [
      'jivu work',
      'jivu tools',
      'tool navigation',
      'tools navigation',
      'open tool',
      'find tool',
      'use tool',
      'jivu command',
      'command center',
      'device command',
      'work mode',
      'lets work',
      'let work',
      'start work',
      'app work',
      'control app',
      'send to desktop',
      'send to mobile',
      'send to other device',
      'send workflow to desktop',
      'handoff workflow',
      'continue workflow',
      'resume workflow',
      'run workflow on desktop',
      'process on desktop',
      'desktop par bhejo',
      'mobile par bhejo',
      'web par bhejo',
      'dusri device',
      'dosri device',
      'device par kaam',
      'machine par kaam',
      'workflow desktop par',
      'kaam continue',
      'kaam resume',
      'jari rakho',
      'machine par chalao',
      'kaam',
      'काम',
      'ऐप',
      'app control'
    ];
    if (workPhrases.some(phrase => normalized.includes(phrase))) {
      return 'work';
    }

    return null;
  }

  private async speakModeChoicePrompt(): Promise<void> {
    this.isPromptingForMode = true;
    this.stopListening();
    this.clearSilenceTimer();

    if (!('speechSynthesis' in window)) {
      this.status.set('idle');
      this.initSpeechRecognition();
      return;
    }

    this.stopSpeaking();
    this.status.set('speaking');

    await this.ensureVoicesLoaded();

    const utterance = new SpeechSynthesisUtterance(this.modeChoicePrompt());
    const speechLanguage = this.resolveTtsLanguage(this.resolveRequestLanguage());
    utterance.lang = speechLanguage;
    this.applyPreferredVoice(utterance, speechLanguage);
    this.currentUtterance = utterance;

    utterance.onend = () => {
      this.currentUtterance = null;
      if (!this.isPromptingForMode || !this.autoRestart) {
        return;
      }

      this.status.set('idle');
      this.finalTranscriptBuffer = '';
      this.transcript.set('');
      this.initSpeechRecognition();
    };

    utterance.onerror = (event) => {
      console.error('TTS Mode Prompt Error', event);
      this.currentUtterance = null;
      if (this.autoRestart) {
        this.status.set('idle');
        this.initSpeechRecognition();
      }
    };

    await new Promise(resolve => setTimeout(resolve, 50));
    window.speechSynthesis.speak(utterance);
  }

  private scheduleAutoSend(): void {
    this.clearSilenceTimer();
    this.silenceTimer = window.setTimeout(() => {
      if (this.status() === 'listening' && this.transcript().trim()) {
        void this.sendToBrain();
      }
    }, 2000);
  }

  private clearSilenceTimer(): void {
    if (!this.silenceTimer) return;
    window.clearTimeout(this.silenceTimer);
    this.silenceTimer = undefined;
  }

  private async handleActionResult(actionResult: VoiceActionRunResult, replyText?: string): Promise<boolean> {
    const hasExecutedActions = actionResult.executedActionIds.length > 0;
    const hasUnsupportedActions = actionResult.unsupportedActionIds.length > 0;
    const isOnlyUnsupported = hasUnsupportedActions && !hasExecutedActions;

    if (isOnlyUnsupported || actionResult.errors.length > 0 && !hasExecutedActions) {
      return false;
    }

    if (!hasExecutedActions) {
      return false;
    }

    const response = replyText || actionResult.spokenText || this.defaultActionReply(actionResult.executedActionIds.length);
    if (response) {
      this.aiResponse.set(response);
      this.isDocked.set(true);
      this.autoRestart = true;
      await this.speakResponse(response);
    }
    return true;
  }

  private requiresConfirmationForAction(action: AssistantAction): boolean {
    return !!action.requiresConfirmation
      || (action.confidence ?? 0) < 0.62
      || action.id === 'confirm_generate_smart_video'
      || action.id === 'byok_test_keys'
      || action.id === 'close_active_dialog';
  }

  private async handlePendingConfirmation(text: string): Promise<void> {
    if (!this.pendingConfirmation) return;

    if (this.isExplicitCancellation(text)) {
      this.pendingConfirmation = null;
      const response = this.confirmationCancelledReply();
      this.aiResponse.set(response);
      await this.speakResponse(response);
      return;
    }

    if (!this.isExplicitAffirmation(text)) {
      const prompt = this.pendingConfirmation.promptText;
      this.aiResponse.set(prompt);
      this.autoRestart = true;
      await this.speakResponse(prompt);
      return;
    }

    const actions = this.pendingConfirmation.actions;
    this.pendingConfirmation = null;
    const actionResult = await this.actionRunner.runActions(actions);
    await this.handleActionResult(actionResult, this.defaultActionReply(actions.length));
  }

  private buildConfirmationPrompt(actions: AssistantAction[], replyText?: string): string {
    const actionNames = actions
      .map(action => action.label || action.id)
      .filter(Boolean)
      .join(', ');
    const base = replyText?.trim() || `Please confirm: ${actionNames}.`;
    return `${base} Say confirm to continue, or cancel to stop.`;
  }

  private isAffirmation(text: string): boolean {
    const normalized = this.normalizeCommandText(text);
    return ['yes', 'confirm', 'do it', 'go ahead', 'continue', 'ok', 'okay', 'start', 'run it']
      .some(phrase => normalized.includes(phrase))
      || normalized.includes('haan')
      || normalized.includes('theek')
      || normalized.includes('ठीक')
      || normalized.includes('हाँ');
  }

  private isCancellation(text: string): boolean {
    const normalized = this.normalizeCommandText(text);
    return ['no', 'cancel', 'stop', 'never mind', 'dont', "don't"].some(phrase => normalized.includes(phrase))
      || normalized.includes('nahi')
      || normalized.includes('नहीं')
      || normalized.includes('रद्द');
  }

  private normalizeCommandText(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\u0600-\u06ff\u0900-\u097f']+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private isExplicitAffirmation(text: string): boolean {
    const normalized = this.normalizeCommandText(text);
    return this.hasCommandPhrase(normalized, [
      'yes',
      'confirm',
      'do it',
      'go ahead',
      'continue',
      'ok',
      'okay',
      'start',
      'run it',
      'haan',
      'theek'
    ]);
  }

  private isExplicitCancellation(text: string): boolean {
    const normalized = this.normalizeCommandText(text);
    return this.hasCommandPhrase(normalized, [
      'no',
      'no thanks',
      'not ok',
      'not okay',
      'cancel',
      'cancel it',
      'stop',
      'never mind',
      'do not',
      'dont',
      "don't",
      'nahi'
    ]);
  }

  private hasCommandPhrase(normalizedText: string, phrases: string[]): boolean {
    if (!normalizedText) return false;
    const words = normalizedText.split(' ');
    return phrases.some((phrase) => {
      const normalizedPhrase = this.normalizeCommandText(phrase);
      if (!normalizedPhrase) return false;
      if (!normalizedPhrase.includes(' ')) {
        return words.includes(normalizedPhrase);
      }
      return normalizedText === normalizedPhrase
        || normalizedText.startsWith(`${normalizedPhrase} `)
        || normalizedText.endsWith(` ${normalizedPhrase}`)
        || normalizedText.includes(` ${normalizedPhrase} `);
    });
  }

  private async fallbackToMainBrain(textToSend: string) {
    // Backward-compatible fallback for old AI test commands
    const parsed = this.aiTestCommands.interpretNaturalLanguage(textToSend);
    if (parsed) {
      const response = await this.aiTestCommands.testFromVoice(textToSend);
      this.aiResponse.set(response);
      await this.speakResponse(response);
      return;
    }

    try {
      const res = await firstValueFrom(this.settingsService.chatBrain(textToSend));
      if (res && res.response) {
        this.aiResponse.set(res.response);
        await this.speakResponse(res.response);
      } else {
        this.status.set('error');
        this.errorMessage.set('Main Brain returned empty response.');
      }
    } catch (err) {
      console.error('Brain chat error', err);
      this.status.set('error');
      this.errorMessage.set('Failed to connect to Brain. Please check API settings.');
    }
  }

  private async sendToShellTalk(message: string): Promise<void> {
    const language = this.resolveRequestLanguage();
    const userId = this.memoryUserId();
    const memory = this.talkMemory.buildContext(userId, language);
    let response: JivuShellTalkResponse;
    try {
      response = await firstValueFrom(this.jivuShell.talk({ message, language, memory }));
    } catch (error: any) {
      if (this.isShellTalkResponse(error?.error)) {
        response = error.error;
      } else {
        throw error;
      }
    }

    await this.handleShellTalkResponse(response, message, userId, language);
  }

  private isShellTalkResponse(value: unknown): value is JivuShellTalkResponse {
    if (!value || typeof value !== 'object') return false;
    const maybeResponse = value as Partial<JivuShellTalkResponse>;
    return typeof maybeResponse.status === 'string';
  }

  private async handleShellTalkResponse(
    response: JivuShellTalkResponse,
    message: string,
    userId: string,
    language: string
  ): Promise<void> {
    this.pendingAction = null;
    this.publishShellStatusChanged();

    if (response.status === 'QuotaExceeded') {
      const reply = response.reply || this.talkQuotaExceededReply(response.quota.limit);
      this.aiResponse.set(reply);
      await this.speakResponse(reply);
      return;
    }

    if (response.status !== 'Success') {
      const reply = response.reply || response.error || this.talkConnectionErrorReply();
      this.aiResponse.set(reply);
      await this.speakResponse(reply);
      return;
    }

    const reply = response.reply || this.talkConnectionErrorReply();
    this.aiResponse.set(reply);
    this.talkMemory.rememberExchange(userId, language, message, reply, response.memorySummary);
    await this.speakResponse(reply);
  }

  private memoryUserId(): string {
    return this.authStore.user()?.userId || 'anonymous';
  }

  private publishShellStatusChanged(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('jivu-shell:status-changed'));
  }

  private isNativeJivuBarAvailable(): boolean {
    return typeof window !== 'undefined'
      && (!!window.heyjivuDesktop?.overlay?.show || !!window.Capacitor?.Plugins?.JivuOverlay?.show);
  }

  private createOverlayBusNonce(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `jivu-overlay-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }


  private defaultActionReply(count: number): string {
    if (!count) return 'Done.';
    return count === 1 ? 'Done.' : 'Done. Executed multiple actions.';
  }

  private defaultClarificationReply(): string {
    return this.uiStore.brainLanguage() === 'ur'
      ? 'Video Editor, Image Editor, AutoCut, Collage, Smart Video, या Jivu Command जैसे किसी tool का नाम बोलिए.'
      : 'Say a tool name such as Video Editor, Image Editor, AutoCut, Collage, Smart Video, or Jivu Command.';
  }

  private confirmationCancelledReply(): string {
    return this.uiStore.brainLanguage() === 'ur'
      ? 'ठीक है, action cancel कर दिया.'
      : 'Okay, I cancelled that action.';
  }

  private voiceActionUnavailableReply(): string {
    return this.uiStore.brainLanguage() === 'ur'
      ? 'Voice action engine अभी available नहीं है. थोड़ी देर बाद कोशिश करें.'
      : 'Voice action engine is unavailable right now. Please try again in a moment.';
  }

  private modeChoicePrompt(): string {
    const isUrduVoice = this.uiStore.brainLanguage() === 'ur';
    if (!this.canUseTalkMode()) {
      return isUrduVoice
        ? 'Tool navigation तैयार है. जिस tool को खोलना है उसका नाम बोलिए.'
        : 'Tool navigation is ready. Say the tool you want to open.';
    }

    return isUrduVoice
      ? 'क्या आप Jivu Talk चाहते हैं या Tool navigation? Tools आपके बताए editor या action को खोलता है.'
      : 'Do you want Talk or Tool navigation? Tools opens the editor or action you ask for.';
  }
  private modeReadyReply(mode: VoiceAssistantMode): string {
    if (this.uiStore.brainLanguage() === 'ur') {
      return mode === 'work'
        ? 'Tool navigation तैयार है. किसी tool का नाम बोलिए.'
        : 'Jivu Talk तैयार है. आप आराम से बात कर सकते हैं.';
    }

    return mode === 'work'
      ? 'Tool navigation is ready. Say a tool name to open it.'
      : 'Jivu Talk is ready. We can just talk.';
  }
  private talkUpgradePrompt(): string {
    return this.uiStore.brainLanguage() === 'ur'
      ? 'Jivu Talk paid plans के लिए है. Tool navigation अभी भी available है.'
      : 'Jivu Talk is for paid plans. Tool navigation is still available.';
  }
  private talkConnectionErrorReply(): string {
    return this.uiStore.brainLanguage() === 'ur'
      ? 'Connection में मसला है. दोबारा कोशिश करें.'
      : 'Connection issue. Please try again.';
  }
  private talkQuotaExceededReply(limit: number): string {
    return this.uiStore.brainLanguage() === 'ur'
      ? `आज की Jivu Talk limit पूरी हो गई है (${limit}). कल फिर बात कर सकते हैं.`
      : `Today's Jivu Talk limit is used (${limit}). You can talk again tomorrow.`;
  }
  private resolveRequestLanguage(): VoiceAssistantLanguage {
    return this.uiStore.brainLanguage() === 'ur' ? 'hi' : 'en';
  }

  private resolveRecognitionLanguage(lang: string): string {
    if (lang === 'en') return 'en-US';
    if (lang === 'ur' || lang === 'ro' || lang === 'hi') return 'hi-IN';
    return 'hi-IN';
  }

  private resolveTtsLanguage(lang: string): string {
    if (lang === 'en') return 'en-US';
    if (lang === 'ur' || lang === 'ro') return 'hi-IN';
    return 'hi-IN';
  }

  private applyPreferredVoice(utterance: SpeechSynthesisUtterance, speechLanguage: string): void {
    const voices = window.speechSynthesis.getVoices();
    const languagePrefix = speechLanguage.split('-')[0].toLowerCase();
    const local = voices.find(v => v.lang.toLowerCase().startsWith(languagePrefix) && v.localService);
    const fallback = voices.find(v => v.lang.toLowerCase().startsWith(languagePrefix));
    utterance.voice = local ?? fallback ?? null;
  }

  private async speakResponse(text: string) {
    const speechLanguage = this.resolveTtsLanguage(this.resolveRequestLanguage());
    this.stopSpeaking();
    if (this.nativeSpeech.speak(text, speechLanguage)) {
      this.nativeSpeaking = true;
      this.status.set('speaking');
      this.clearNativeTtsWatchdog();
      this.nativeTtsWatchdog = globalThis.setTimeout(
        () => this.finishSpeechCycle(),
        Math.min(120000, 5000 + text.length * 120)
      );
      return;
    }

    if (!('speechSynthesis' in window)) {
      this.status.set('idle');
      return;
    }

    this.status.set('speaking');

    await this.ensureVoicesLoaded();
    this.currentUtterance = new SpeechSynthesisUtterance(text);

    this.currentUtterance.lang = speechLanguage;
    this.applyPreferredVoice(this.currentUtterance, speechLanguage);

    this.currentUtterance.onend = () => this.finishSpeechCycle();

    this.currentUtterance.onerror = (e) => {
      console.error('TTS Speak Error', e);
      this.status.set('idle');
    };

    // Slight delay to let the browser settle between cancel + speak
    await new Promise(r => setTimeout(r, 50));
    window.speechSynthesis.speak(this.currentUtterance);
  }

  private ensureVoicesLoaded(): Promise<void> {
    return new Promise(resolve => {
      if (window.speechSynthesis.getVoices().length > 0) {
        resolve();
        return;
      }
      window.speechSynthesis.onvoiceschanged = () => resolve();
      // Timeout fallback after 3s
      setTimeout(resolve, 3000);
    });
  }

  stopSpeaking() {
    this.clearNativeTtsWatchdog();
    if (this.nativeSpeaking) {
      this.nativeSpeaking = false;
      this.nativeSpeech.stopSpeaking();
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }

  onOverlayClick(): void {
    if (!this.isDocked()) {
      this.close();
    }
  }

  onCardClick(event: MouseEvent): void {
    event.stopPropagation();

    if (this.isDocked()) {
      this.expandPopup();
    }
  }

  closeFromButton(event: MouseEvent): void {
    event.stopPropagation();
    this.close();
  }

  expandPopup() {
    this.isDocked.set(false);
  }

  close() {
    this.isDocked.set(false);
    this.autoRestart = false;
    this.isPromptingForMode = false;
    this.clearSilenceTimer();
    this.clearRecognitionRestart();
    this.clearNativeTtsWatchdog();
    this.stopSpeaking();
    this.stopListening();
    this.uiStore.closeGlobalMic();
  }

  private handleNativeSpeechEvent(payload: NativeSpeechPayload): void {
    if (payload.type === 'tts-end') {
      this.finishSpeechCycle();
      return;
    }

    if (!this.nativeListening) return;

    if (payload.type === 'result' && payload.text?.trim()) {
      const text = payload.text.trim();
      if (payload.final !== false) {
        this.finalTranscriptBuffer = `${this.finalTranscriptBuffer} ${text}`.trim();
      }
      this.transcript.set(payload.final === false
        ? `${this.finalTranscriptBuffer} ${text}`.trim()
        : this.finalTranscriptBuffer);
      this.noSpeechCount = 0;
      this.scheduleAutoSend();
      return;
    }

    if (payload.type === 'error') {
      const error = payload.error || 'unavailable';
      if (error === 'no_match' || error === 'speech_timeout' || error === 'no_speech' || error === 'busy') {
        this.handleNoSpeech();
        return;
      }
      this.status.set('error');
      this.errorMessage.set(`Speech recognition failed: ${error}`);
      return;
    }

    if (payload.type === 'end' && this.status() === 'listening' && this.autoRestart) {
      this.scheduleRecognitionRestart(this.noSpeechCount > 0 ? 500 : 0);
    }
  }

  private handleNoSpeech(): void {
    this.noSpeechCount++;
    if (this.noSpeechCount >= this.maxNoSpeechRestarts) {
      this.noSpeechCount = 0;
      this.autoRestart = false;
      this.status.set('error');
      this.errorMessage.set('No speech detected. Tap the mic and speak.');
    }
  }

  private scheduleRecognitionRestart(delayMs: number): void {
    this.clearRecognitionRestart();
    this.recognitionRestartTimer = window.setTimeout(() => {
      if (!this.autoRestart || this.status() !== 'listening') return;
      if (this.nativeListening) {
        this.nativeListening = false;
        this.initSpeechRecognition();
        return;
      }
      try { this.recognition?.start(); } catch (e) {}
    }, delayMs);
  }

  private clearRecognitionRestart(): void {
    if (this.recognitionRestartTimer === undefined) return;
    window.clearTimeout(this.recognitionRestartTimer);
    this.recognitionRestartTimer = undefined;
  }

  private finishSpeechCycle(): void {
    this.currentUtterance = null;
    this.nativeSpeaking = false;
    this.clearNativeTtsWatchdog();
    if (this.autoRestart) {
      this.finalTranscriptBuffer = '';
      this.transcript.set('');
      this.aiResponse.set('');
      this.initSpeechRecognition();
    } else {
      this.status.set('idle');
    }
  }

  private clearNativeTtsWatchdog(): void {
    if (this.nativeTtsWatchdog === undefined) return;
    globalThis.clearTimeout(this.nativeTtsWatchdog);
    this.nativeTtsWatchdog = undefined;
  }
}
